import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { ColumnDef, Row } from '@tanstack/react-table';
import { ToggleButtonGroup } from '@mui/material';
import { StyledToggleButton } from 'components/StyledToggleButton';
import { PageContainer, Tile } from 'components/Layout/PageContainer';
import { DataTable } from 'components/table/DataTable';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { MeasureCardRow } from 'components/MeasureCardRow';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { FilterAttributeCode } from 'hooks/state/DisabledFiltersSlice';
import { FilterValue } from 'components/breadcrumb/breadcrumbModel';
import { ExtendedAttributeCode } from 'pages/trading/model';
import { toFeesAndCostsRateMeasure, useProfitabilityMeasures } from 'components/profitability/profitabilityMeasures';
import { ProfitabilityWaterfallChart } from 'components/profitability/ProfitabilityWaterfallChart';
import { FeesList } from 'components/profitability/FeesList';
import { LogisticsCostsList } from 'components/profitability/LogisticsCostsList';
import { ExploreMoreSection } from '../components/ExploreMoreSection';
import { useProfitabilityColumns } from 'components/profitability/buildProfitabilityColumns';
import {
  useProfitabilityDrillDownKpis,
  useProfitabilityFeesAndCosts,
  useProfitabilityTableData,
  useRequestDataForDrillDownPage,
} from 'components/profitability/queryHooks';
import { ProfitabilityBreakdownRow } from 'components/profitability/types';
import { DrillDownTitle } from 'components/DrillDownTitle';
import { AttributeCode, useModelService } from 'services/model/model.service';
import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { ProfitabilityBreakdownInfoContent } from 'components/profitability/ProfitabilityBreakdownInfoContent';
import { useInfoDrawer } from 'context/InfoDrawerContext';

export function ProfitabilityDrillDownPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const datePickerLabels = useDatePickerLabels();

  const { coreAttributeCode = 'prdCategory', coreAttributeValue = '' } = useParams<{
    coreAttributeCode: AttributeCode;
    coreAttributeValue: string;
  }>();

  const { getAttribute } = useModelService();
  getAttribute(coreAttributeCode);

  const { setDetailPageBreadcrumb } = useBreadcrumbStore();
  const transitiveCacheStore = useTransitiveCacheStore();

  const { setContent } = useInfoDrawer();

  useEffect(() => {
    setContent({
      titleKey: 'profitability.deepDive.title',
      content: <ProfitabilityBreakdownInfoContent />,
    });
    return () => setContent(null);
  }, [setContent]);

  const [filters, setFilters] = useState<Record<ExtendedAttributeCode, FilterValue[]>>(
    {} as Record<ExtendedAttributeCode, FilterValue[]>
  );

  const lockedAttributeCodes = Object.keys(filters) as FilterAttributeCode[];

  const hasProductFilter = lockedAttributeCodes.includes('prdNumber');

  const availableDimensions = (
    hasProductFilter
      ? (['chnMarketplace', 'chnCountry', 'chnFulfillmentMethod', 'styBrandColor'] as AttributeCode[])
      : (['prdCategory', 'chnMarketplace', 'chnCountry', 'chnFulfillmentMethod', 'prdNumber'] as AttributeCode[])
  ).filter(dim => !lockedAttributeCodes.includes(dim));

  const defaultDimension = hasProductFilter ? 'styBrandColor' : (availableDimensions[0] ?? null);
  const [selectedDimension, setSelectedDimension] = useState<AttributeCode | null>(null);
  const attributeCode: AttributeCode | null =
    selectedDimension !== null && availableDimensions.includes(selectedDimension)
      ? selectedDimension
      : defaultDimension;

  useEffect(() => {
    if (coreAttributeValue && coreAttributeCode) {
      setFilters(
        setDetailPageBreadcrumb(location.pathname, [
          { attribute: coreAttributeCode, values: [decodeURIComponent(coreAttributeValue)] },
        ])
      );
    }
  }, [coreAttributeValue, location.pathname, coreAttributeCode, setDetailPageBreadcrumb]);

  useEffect(() => {
    transitiveCacheStore.setDisabledFilters(lockedAttributeCodes);
    return () => {
      transitiveCacheStore.enableAllFilters();
    };
  }, [filters]);

  const kpiQueryKey = `profitability-drilldown-kpis-${JSON.stringify(filters)}`;
  const {
    data: rawKpiData,
    isLoading: isKpiLoading,
    isError: isKpiError,
    error: kpiError,
  } = useProfitabilityDrillDownKpis(filters, kpiQueryKey);

  const kpiData = rawKpiData ?? null;

  const breakdownQueryKey = `profitability-drilldown-${attributeCode}-${JSON.stringify(filters)}`;

  const requestData = useRequestDataForDrillDownPage(filters);

  const { data: feesAndCostsData } = useProfitabilityFeesAndCosts(requestData);

  const {
    data: breakdownData,
    isLoading: isBreakdownLoading,
    isError: isBreakdownError,
    error: breakdownError,
  } = useProfitabilityTableData(attributeCode ?? 'prdCategory', requestData, breakdownQueryKey);

  const [netSoldGmv, cm1Rate, slsFeeRate, cm2Rate, netSlsLogCostRate] = useProfitabilityMeasures();
  const measures = [netSoldGmv, cm1Rate, toFeesAndCostsRateMeasure(slsFeeRate, netSlsLogCostRate), cm2Rate];

  const { getAttributeArrayForLeadAttribute, createAttributeColumn } = useDataEntriesInTables();
  const tableAttributes = getAttributeArrayForLeadAttribute(attributeCode);
  const attributeColumns = tableAttributes.map(attribute => createAttributeColumn(attribute));
  const profitabilityColumns = useProfitabilityColumns();

  const columns = [...attributeColumns, ...profitabilityColumns] as ColumnDef<ProfitabilityBreakdownRow>[];

  const isStyleLevel = attributeCode === 'styBrandColor';

  const handleRowClick = isStyleLevel
    ? undefined
    : (row: Row<ProfitabilityBreakdownRow>) => {
        const dimensionValue = encodeURIComponent(row.original[attributeCode] ?? '');
        navigate(`/profitability/drilldown/${attributeCode}/${dimensionValue}${location.search}`);
      };

  const tableTitleId = `profitability.drillDown.dimension.${attributeCode}`;

  return (
    <div className="px-10 max-w-none">
      <PageContainer>
        <Tile className="px-4 py-2">
          <div className="flex flex-row items-center gap-3">
            <span className="text-2xl font-bold">
              <DrillDownTitle filterAttributes={filters} />
            </span>
            <span className="text-xs text-ash-500">
              {datePickerLabels.dateRangeLabel ? intl.formatMessage({ id: datePickerLabels.dateRangeLabel }) : ''}
            </span>
          </div>
        </Tile>

        {isKpiError ? (
          <Tile errorDetails={kpiError} testId="profitability-drilldown-kpis-error" />
        ) : isKpiLoading || !kpiData ? (
          <Tile>
            <MetricsLoadingSpinner />
          </Tile>
        ) : (
          <>
            <MeasureCardRow
              measures={measures}
              totalsData={kpiData}
              customComparisonPeriod={intl.formatMessage({ id: 'datepicker.options.comparison.previous.year.week' })}
              testid="profitability-drilldown-measure-card-row"
            />
            <ProfitabilityWaterfallChart
              data={kpiData}
              comparisonPeriod="datepicker.options.comparison.previous.year.week"
            />

            {feesAndCostsData && (
              <>
                <FeesList
                  values={feesAndCostsData.values}
                  compareValues={feesAndCostsData.compareValues}
                  isComparisonActive
                />
                <LogisticsCostsList
                  values={feesAndCostsData.values}
                  compareValues={feesAndCostsData.compareValues}
                  isComparisonActive
                />
              </>
            )}
          </>
        )}

        <div className="xl:col-span-12 col-span-6 flex flex-col gap-4">
          {availableDimensions.length > 1 && (
            <ToggleButtonGroup
              className="mb-auto mt-auto"
              exclusive
              value={attributeCode}
              onChange={(_event, value: AttributeCode | null) => {
                if (value !== null) setSelectedDimension(value);
              }}
            >
              {availableDimensions.map(dim => (
                <StyledToggleButton
                  key={dim}
                  value={dim}
                  size="small"
                  className="whitespace-nowrap text-xs"
                  sx={{ color: '#1A1A1A' }}
                >
                  {intl.formatMessage({ id: `profitability.drillDown.dimension.${dim}` })}
                </StyledToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {isBreakdownError ? (
            <Tile errorDetails={breakdownError} testId="profitability-drilldown-breakdown-error" />
          ) : isBreakdownLoading || !breakdownData ? (
            <Tile>
              <MetricsLoadingSpinner />
            </Tile>
          ) : (
            <DataTableContextProvider>
              <DataTable
                titleId={tableTitleId}
                rows={breakdownData}
                columns={columns}
                defaultSortingState={[{ id: 'netSlsGmvValExtExpOD', desc: true }]}
                onRowClick={handleRowClick}
                exportConfigOptions={{
                  exportName: `profitability-drilldown-${attributeCode}`,
                  skippedColumns: ['artImageUrl'],
                  customHeaderExporters: {
                    status: exportIntl => exportIntl.formatMessage({ id: 'profitability.drillDown.column.status' }),
                  },
                }}
                testId={`profitability-drilldown-${attributeCode}-table`}
              />
            </DataTableContextProvider>
          )}
        </div>

        <ExploreMoreSection filters={filters} />
      </PageContainer>
    </div>
  );
}
